/**
 * The issue queue.
 *
 * `auth_rules_skipped` is stated at the top, before any count, and the
 * "nothing found" state is worded differently depending on it. A partial report
 * that renders as an empty list is indistinguishable from a clean bill of
 * health, and that misreading is exactly what the flag exists to prevent.
 *
 * What changed: findings used to be values recomputed on every request, so this
 * page could only ever show a list. They are now reconciled against a table, so
 * each one has an id, an age, a status and an assignee — which is what makes
 * this a queue rather than a report. Two consequences the UI must respect:
 *
 *   - `persisted: false` means the controller has no database and findings are
 *     computed-only. The acknowledge and resolve controls are then not merely
 *     disabled, they are absent, because there is nothing for them to write to.
 *
 *   - `skipped_families` names rule families that did not run this pass. Those
 *     were deliberately NOT auto-resolved, and saying so is what stops a
 *     partial pass reading as an all-clear.
 *
 * Manual resolve does not stop the validator emitting a finding, so the next
 * pass reopens it. The button says so rather than pretending otherwise: closing
 * a ticket does not fix a config.
 *
 * ── the compaction pass ─────────────────────────────────────────────────────
 *
 * The operator reading this page opens it a dozen times a day and does not need
 * to be told what a validator is, so the header's descriptive subtitle is gone.
 * Both honesty callouts stay exactly as they were: they render only when the
 * thing they warn about is true, so on a normal day nothing sits between the
 * title and the numbers, and the queue table is above the fold.
 *
 * The KPI strip became the filter control. Blocking, Warnings, Info and
 * Acknowledged each name a subset of the queue, and each is now the button that
 * narrows the queue to it — pressing an active one returns to the default open
 * view, which is why they carry `aria-pressed` rather than being links. They
 * are buttons only when `persisted`: without a database there is no queue for a
 * click to filter, so the same numbers render as plain tiles instead of
 * controls that would do nothing. Opened/Resolved (24h) stay plain throughout —
 * they are movement over time, not a slice of the list, and there is no filter
 * they could set.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge, SeverityBadge, severityLabel } from '../components/Badge';
import { Callout } from '../components/Callout';
import { PageHeader } from '../components/PageHeader';
import { StatGrid, StatTile, type MetricTone } from '../components/StatTile';
import { EmptyBlock, ErrorBlock, SkeletonRows, StaleBanner } from '../components/States';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader, KeyValue, KeyValueRow } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { DetailPanel } from '../components/ui/DetailPanel';
import { FilterBar, SegmentedFilter } from '../components/ui/FilterBar';
import { ChevronRightIcon, RenewIcon } from '../components/ui/Icon';
import { apiPut, describeError } from '../lib/api';
import { formatNumber, formatRelative, pluralise, UNKNOWN } from '../lib/format';
import { useResource } from '../lib/useResource';
import type {
  IssueListResponse,
  IssueResponse,
  IssuesResponse,
  IssueStatus,
  Severity,
} from '../types/api';

import '../styles/page-issues.css';

type StatusFilter = 'open' | 'acknowledged' | 'resolved' | 'all';

const SEVERITY_RANK: Record<string, number> = { block: 0, warn: 1, info: 2 };

export function IssuesPage() {
  // The live report: always available, works with no database. It is also what
  // drives reconciliation server-side, so asking for it keeps the queue current.
  const report = useResource<IssuesResponse>('/api/issues', { refreshMs: 60_000 });
  const persisted = report.data?.persisted ?? false;

  const [status, setStatus] = useState<StatusFilter>('open');
  const [severity, setSeverity] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);

  // The queue only exists when findings are persisted; asking for it otherwise
  // would 503 on every render.
  const queue = useResource<IssueListResponse>(
    `/api/issues/queue${status === 'all' ? '' : `?status=${status}`}`,
    { enabled: persisted, refreshMs: 60_000 },
  );

  const rows = useMemo(() => {
    const all = queue.data?.issues ?? [];
    return severity === 'all' ? all : all.filter((i) => i.severity === severity);
  }, [queue.data, severity]);

  const counts = report.data?.queue ?? {};
  const openTotal =
    (counts.block_open ?? 0) + (counts.warn_open ?? 0) + (counts.info_open ?? 0);
  const openedDelta = (counts.opened_24h ?? 0) - (counts.opened_prev_24h ?? 0);

  // The queue's own totals when there is a queue, the live report's otherwise —
  // the same fallback the tiles have always used, lifted out so the tile and the
  // filter it sets are driven by one number.
  const reportCounts = report.data?.counts;
  const blockOpen = counts.block_open ?? reportCounts?.block ?? 0;
  const warnOpen = counts.warn_open ?? reportCounts?.warn ?? 0;
  const infoOpen = counts.info_open ?? reportCounts?.info ?? 0;

  const open = openId != null ? rows.find((i) => i.id === openId) : undefined;

  /**
   * Set the pair of filters a KPI stands for, or clear them if it is already
   * the current view. A tile that only ever narrows leaves the operator hunting
   * for the segmented control to get back, which is the opposite of the point.
   */
  function applyFilter(nextStatus: StatusFilter, nextSeverity: string, active: boolean) {
    setStatus(active ? 'open' : nextStatus);
    setSeverity(active ? 'all' : nextSeverity);
  }

  function reloadAll() {
    report.reload();
    queue.reload();
  }

  return (
    <>
      <PageHeader
        title="Issues"
        status={
          report.data ? (
            <Badge tone={(counts.block_open ?? 0) > 0 ? 'critical' : openTotal > 0 ? 'warning' : 'good'}>
              {openTotal === 0
                ? 'Nothing open'
                : `${openTotal} open ${pluralise(openTotal, 'issue')}`}
            </Badge>
          ) : null
        }
        actions={
          <Button kind="tertiary" size="sm" renderIcon={RenewIcon} onClick={reloadAll}>
            Re-run validator
          </Button>
        }
      />

      {/* ── the two honesty flags, before any number ───────────────────── */}
      {report.data?.auth_rules_skipped ? (
        <Callout tone="warning" title="This report is incomplete — auth rules did not run">
          <p>
            <code>caddy-auth.env</code> could not be read, so the checks for missing,
            malformed and plaintext basic-auth material were skipped. Their findings are
            absent because they were <strong>not looked for</strong>, not because they
            passed.
          </p>
          {persisted ? (
            <p>
              Existing auth issues were deliberately left open rather than auto-resolved,
              for the same reason.
            </p>
          ) : null}
        </Callout>
      ) : null}

      {!persisted && report.data ? (
        <Callout tone="info" title="Findings are computed, not tracked">
          <p>
            Without the <code>fleet_control</code> database this page can show what is
            wrong, but not since when, nor what anyone decided about it. Acknowledge and
            resolve are unavailable because there is nowhere to record them.
          </p>
        </Callout>
      ) : null}

      {/* ── counts ─────────────────────────────────────────────────────── */}
      {report.loading && !report.data ? (
        <SkeletonRows rows={2} />
      ) : report.error && !report.data ? (
        <ErrorBlock what="the validator report" error={report.error} onRetry={report.reload} />
      ) : report.data ? (
        <div className="stack">
          {report.error ? <StaleBanner error={report.error} onRetry={report.reload} /> : null}
          <StatGrid>
            <FilterTile
              label="Blocking"
              value={formatNumber(blockOpen)}
              tone={blockOpen > 0 ? 'critical' : 'neutral'}
              hint="blocks an apply"
              filterable={persisted}
              active={status === 'open' && severity === 'block'}
              onPick={(active) => applyFilter('open', 'block', active)}
            />
            <FilterTile
              label="Warnings"
              value={formatNumber(warnOpen)}
              tone={warnOpen > 0 ? 'warning' : 'neutral'}
              hint="safe to apply, worth fixing"
              filterable={persisted}
              active={status === 'open' && severity === 'warn'}
              onPick={(active) => applyFilter('open', 'warn', active)}
            />
            <FilterTile
              label="Info"
              value={formatNumber(infoOpen)}
              hint="observations only"
              filterable={persisted}
              active={status === 'open' && severity === 'info'}
              onPick={(active) => applyFilter('open', 'info', active)}
            />
            {persisted ? (
              <>
                <FilterTile
                  label="Acknowledged"
                  value={formatNumber(counts.acknowledged ?? 0)}
                  hint="seen by a human"
                  filterable
                  active={status === 'acknowledged' && severity === 'all'}
                  onPick={(active) => applyFilter('acknowledged', 'all', active)}
                />
                <StatTile
                  label="Opened (24h)"
                  value={formatNumber(counts.opened_24h ?? 0)}
                  tone={openedDelta > 0 ? 'warning' : 'neutral'}
                  hint={
                    openedDelta === 0
                      ? 'same as the day before'
                      : `${openedDelta > 0 ? '+' : ''}${openedDelta} vs the day before`
                  }
                />
                <StatTile
                  label="Resolved (24h)"
                  value={formatNumber(counts.resolved_24h ?? 0)}
                  tone={(counts.resolved_24h ?? 0) > 0 ? 'good' : 'neutral'}
                  hint="fixed or closed by hand"
                />
              </>
            ) : null}
          </StatGrid>
        </div>
      ) : null}

      {/* ── the queue ──────────────────────────────────────────────────── */}
      {persisted ? (
        <QueueTable
          queue={queue}
          rows={rows}
          search={search}
          onSearch={setSearch}
          status={status}
          onStatus={setStatus}
          severity={severity}
          onSeverity={setSeverity}
          counts={counts}
          onOpen={setOpenId}
          openId={openId}
          authSkipped={report.data?.auth_rules_skipped ?? false}
        />
      ) : report.data ? (
        <ComputedOnlyList findings={report.data.findings} />
      ) : null}

      <DetailPanel
        open={open !== undefined}
        title={open?.code ?? ''}
        subtitle={open?.center_slug ? `on ${open.center_slug}` : 'fleet-wide'}
        onClose={() => setOpenId(null)}
      >
        {open ? <IssuePanel issue={open} onChanged={reloadAll} /> : null}
      </DetailPanel>
    </>
  );
}

/**
 * A KPI that is also the filter for the rows it counts.
 *
 * `filterable` is not styling — it is whether there is a queue to narrow. With
 * no database the queue endpoint is never called and the table below is the
 * computed-only list, so a button here would be a control that visibly does
 * nothing; the number renders as an ordinary tile instead.
 *
 * A button rather than a link because this filters the list in place: there is
 * no URL that differs, so a link would lie about what middle-click does.
 * `aria-pressed` carries the on/off state, which is also what makes pressing an
 * active tile to clear it the expected behaviour rather than a surprise.
 */
function FilterTile({
  label,
  value,
  hint,
  tone,
  filterable,
  active,
  onPick,
}: {
  label: string;
  value: string;
  hint: string;
  tone?: MetricTone;
  filterable: boolean;
  active: boolean;
  onPick: (active: boolean) => void;
}) {
  if (!filterable) {
    return <StatTile label={label} value={value} hint={hint} tone={tone} />;
  }
  return (
    <button
      type="button"
      className={`metric issues-kpi${tone ? ` metric--${tone}` : ''}`}
      aria-pressed={active}
      // A description, not a name: the tile's own text ("Blocking 3 blocks an
      // apply") is the name and is worth more than a verb. This says what
      // pressing does, which the tone and the number cannot.
      title={
        active
          ? `Showing ${label.toLowerCase()} only — press to show everything open`
          : `Show ${label.toLowerCase()} only`
      }
      onClick={() => onPick(active)}
    >
      <span className="metric__head">
        <span className="metric__label">{label}</span>
      </span>
      <span className="metric__value">{value}</span>
      <span className="metric__hint">{hint}</span>
    </button>
  );
}

function QueueTable({
  queue,
  rows,
  search,
  onSearch,
  status,
  onStatus,
  severity,
  onSeverity,
  counts,
  onOpen,
  openId,
  authSkipped,
}: {
  queue: ReturnType<typeof useResource<IssueListResponse>>;
  rows: IssueResponse[];
  search: string;
  onSearch: (v: string) => void;
  status: StatusFilter;
  onStatus: (v: StatusFilter) => void;
  severity: string;
  onSeverity: (v: string) => void;
  counts: Record<string, number>;
  onOpen: (id: number) => void;
  openId: number | null;
  authSkipped: boolean;
}) {
  const columns: ReadonlyArray<Column<IssueResponse, string>> = useMemo(
    () => [
      {
        key: 'severity',
        header: 'Severity',
        value: (r) => SEVERITY_RANK[r.severity] ?? 9,
        render: (r) => <SeverityBadge severity={r.severity as Severity} small />,
      },
      {
        key: 'code',
        header: 'Issue',
        render: (r) => (
          <div className="cell-stack">
            <span className="cell-stack__primary">{r.code}</span>
            <span className="cell-stack__secondary">{r.message}</span>
          </div>
        ),
      },
      {
        key: 'center_slug',
        header: 'Center',
        render: (r) =>
          r.center_slug ? (
            <Link to={`/centers/${r.center_slug}`}>{r.center_slug}</Link>
          ) : (
            // Fleet-wide is a real scope, not a missing value.
            <span className="muted">Fleet-wide</span>
          ),
      },
      {
        key: 'first_seen_at',
        header: 'Age',
        value: (r) => r.first_seen_at,
        render: (r) => (
          <span title={r.first_seen_at}>{formatRelative(r.first_seen_at)}</span>
        ),
      },
      {
        key: 'assignee_email',
        header: 'Owner',
        render: (r) =>
          r.assignee_email ? (
            <span className="cell-stack__secondary">{r.assignee_email}</span>
          ) : (
            <span className="muted">Unassigned</span>
          ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (r) => <StatusPill status={r.status} resolution={r.resolution} />,
      },
    ],
    [],
  );

  return (
    <>
      <FilterBar
        search={search}
        onSearch={onSearch}
        searchPlaceholder="Search code, message, center…"
        searchLabel="Search issues"
        count={`${formatNumber(rows.length)} shown`}
      >
        <SegmentedFilter<StatusFilter>
          legend="Filter by status"
          value={status}
          onChange={onStatus}
          options={[
            { value: 'open', label: 'Open', count: counts.block_open != null
                ? (counts.block_open ?? 0) + (counts.warn_open ?? 0) + (counts.info_open ?? 0)
                : undefined },
            { value: 'acknowledged', label: 'Acknowledged', count: counts.acknowledged },
            { value: 'resolved', label: 'Resolved' },
            { value: 'all', label: 'All' },
          ]}
        />
        <SegmentedFilter<string>
          legend="Filter by severity"
          value={severity}
          onChange={onSeverity}
          options={[
            { value: 'all', label: 'Any' },
            { value: 'block', label: 'Blocking' },
            { value: 'warn', label: 'Warning' },
            { value: 'info', label: 'Info' },
          ]}
        />
      </FilterBar>

      {queue.loading && !queue.data ? (
        <SkeletonRows rows={4} />
      ) : queue.error && !queue.data ? (
        <ErrorBlock what="the issue queue" error={queue.error} onRetry={queue.reload} />
      ) : (
        <Card flush>
          <DataTable<IssueResponse, string>
            rows={rows}
            columns={columns}
            getRowId={(r) => String(r.id)}
            label="Issue queue"
            initialSortKey="severity"
            search={search}
            onRowClick={(r) => onOpen(r.id)}
            isRowActive={(r) => r.id === openId}
            empty={
              <EmptyBlock
                title={
                  authSkipped
                    ? 'Nothing found — but the report is incomplete'
                    : status === 'open'
                      ? 'Nothing open'
                      : 'Nothing here'
                }
              >
                {authSkipped
                  ? 'The auth rules did not run, so this is not a clean bill of health. Fix the auth material and re-run before trusting an empty queue.'
                  : status === 'open'
                    ? 'Every rule that ran passed, and nothing is waiting on a human.'
                    : 'No issue has that status right now.'}
              </EmptyBlock>
            }
            emptyFiltered={
              <EmptyBlock title="Nothing matches those filters">
                Try a different status or severity.
              </EmptyBlock>
            }
          />
        </Card>
      )}
    </>
  );
}

function StatusPill({
  status,
  resolution,
}: {
  status: IssueStatus;
  resolution: 'fixed' | 'manual' | null;
}) {
  if (status === 'resolved') {
    return (
      <Badge
        tone="good"
        small
        title={
          resolution === 'manual'
            ? 'Closed by hand while the validator was still emitting it — the next pass will reopen it.'
            : 'The validator stopped emitting this, i.e. the configuration was corrected.'
        }
      >
        {resolution === 'manual' ? 'Closed by hand' : 'Fixed'}
      </Badge>
    );
  }
  if (status === 'acknowledged') {
    return <Badge tone="info" small>Acknowledged</Badge>;
  }
  return <Badge tone="warning" small>Open</Badge>;
}

/** The detail panel: diagnostics, context, and the one remediation control. */
function IssuePanel({ issue, onChanged }: { issue: IssueResponse; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function setStatus(status: IssueStatus) {
    setBusy(true);
    setError(null);
    try {
      await apiPut(`/api/issues/${issue.id}/status`, { status });
      onChanged();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error ? (
        <Callout tone="critical" title="Could not update this issue">
          {error}
        </Callout>
      ) : null}

      <section className="stack">
        <div className="row">
          <SeverityBadge severity={issue.severity as Severity} />
          <StatusPill status={issue.status} resolution={issue.resolution} />
        </div>
        <p className="panel-issue__message">{issue.message}</p>
        {issue.fix_hint ? (
          <Callout tone="info" title="Suggested fix">
            {issue.fix_hint}
          </Callout>
        ) : null}
      </section>

      <section className="stack">
        <h3 className="section__subtitle">Diagnostics</h3>
        <KeyValue>
          <KeyValueRow label="Rule">
            <span className="mono">{issue.code}</span>
          </KeyValueRow>
          <KeyValueRow label="Severity">{severityLabel(issue.severity as Severity)}</KeyValueRow>
          <KeyValueRow label="Field">
            {issue.field ? <span className="mono">{issue.field}</span> : <span className="muted">{UNKNOWN}</span>}
          </KeyValueRow>
          <KeyValueRow label="Center">
            {issue.center_slug ? (
              <Link to={`/centers/${issue.center_slug}`}>{issue.center_slug}</Link>
            ) : (
              <span className="muted">Fleet-wide</span>
            )}
          </KeyValueRow>
          <KeyValueRow label="First seen">
            <span title={issue.first_seen_at}>{formatRelative(issue.first_seen_at)}</span>
          </KeyValueRow>
          <KeyValueRow label="Last seen">
            <span title={issue.last_seen_at}>{formatRelative(issue.last_seen_at)}</span>
          </KeyValueRow>
          <KeyValueRow label="Owner">
            {issue.assignee_email ?? <span className="muted">Unassigned</span>}
          </KeyValueRow>
          {issue.acknowledged_by ? (
            <KeyValueRow label="Acknowledged by">
              {issue.acknowledged_by}{' '}
              <span className="muted">{formatRelative(issue.acknowledged_at)}</span>
            </KeyValueRow>
          ) : null}
          {issue.resolved_at ? (
            <KeyValueRow label="Resolved">
              {issue.resolved_by ?? 'automatically'}{' '}
              <span className="muted">{formatRelative(issue.resolved_at)}</span>
            </KeyValueRow>
          ) : null}
        </KeyValue>
      </section>

      <section className="stack">
        <h3 className="section__subtitle">Remediation</h3>
        {issue.status === 'resolved' ? (
          <>
            <p className="muted">
              {issue.resolution === 'manual'
                ? 'This was closed by hand. If the validator still emits it, the next pass will reopen it.'
                : 'The validator stopped emitting this, so the configuration was corrected.'}
            </p>
            <div className="row">
              <Button kind="tertiary" size="sm" disabled={busy} onClick={() => setStatus('open')}>
                Reopen
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="field__help muted">
              Acknowledging records that someone has seen this; it stays in the queue.
              Resolving by hand closes it, but does <strong>not</strong> change the
              configuration — if the validator still finds it, the next pass reopens it.
            </p>
            <div className="row">
              {issue.status !== 'acknowledged' ? (
                <Button
                  kind="primary"
                  size="sm"
                  disabled={busy}
                  onClick={() => setStatus('acknowledged')}
                >
                  Acknowledge
                </Button>
              ) : null}
              <Button
                kind="tertiary"
                size="sm"
                disabled={busy}
                onClick={() => setStatus('resolved')}
              >
                Resolve by hand
              </Button>
              {issue.center_slug ? (
                <Link to={`/centers/${issue.center_slug}`} className="btn btn--ghost btn--sm">
                  Open center
                  <ChevronRightIcon size={14} />
                </Link>
              ) : null}
            </div>
          </>
        )}
      </section>
    </>
  );
}

/** The no-database fallback: the report, grouped, with no controls. */
function ComputedOnlyList({ findings }: { findings: IssuesResponse['findings'] }) {
  if (findings.length === 0) {
    return (
      <EmptyBlock title="No findings">
        Every rule that ran passed against the current configuration.
      </EmptyBlock>
    );
  }
  return (
    <Card flush>
      <CardHeader title="Findings" count={findings.length} subtitle="Computed now — not tracked over time." />
      <CardBody flush>
        <ul className="panel-issues issues-computed">
          {findings.map((f, i) => (
            <li key={`${f.code}-${i}`} className="panel-issue">
              <div className="row">
                <SeverityBadge severity={f.severity as Severity} small />
                <span className="mono panel-issue__code">{f.code}</span>
                {f.center_slug ? (
                  <Link to={`/centers/${f.center_slug}`}>{f.center_slug}</Link>
                ) : (
                  <span className="muted">Fleet-wide</span>
                )}
              </div>
              <p className="panel-issue__message">{f.message}</p>
              {f.fix_hint ? <p className="panel-issue__hint">{f.fix_hint}</p> : null}
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}
